import type { CSSProperties } from "react";
import type { ITheme } from "../types/theme";

/**
 * Expone el theme de la tienda como variables CSS.
 *
 * El problema que resuelve: el theme llega a cada widget como prop, pero los
 * estilos viven en módulos SCSS, que no ven las props. El resultado es que
 * varios componentes traen sus colores hardcodeados y **ignoran la
 * personalización del vendedor**: con una plantilla verde, la portada se adapta
 * y los beneficios se quedan en blanco y gris.
 *
 * Declarando las variables en un contenedor, cualquier SCSS descendiente puede
 * leerlas sin recibir props:
 *
 *     background-color: var(--sumer-background, #ffffff);
 *
 * El segundo argumento de `var()` es obligatorio por convención: es el valor
 * que tenía el componente antes de migrarlo. Así, si el theme no está definido
 * —un componente fuera del catálogo, un test, un render parcial— el aspecto no
 * cambia.
 *
 * Solo se exponen los colores de MARCA. Los grises estructurales, bordes y
 * sombras no salen de aquí: no dependen de la tienda.
 */
export function themeCssVars(theme?: Partial<ITheme> | null): CSSProperties {
  const colors = theme?.themeColors;
  const vars: Record<string, string> = {};

  if (colors?.background) vars["--sumer-background"] = colors.background;
  if (colors?.button) vars["--sumer-button"] = colors.button;
  if (colors?.text) vars["--sumer-text"] = colors.text;
  if (theme?.fontFamily) vars["--sumer-font"] = theme.fontFamily;

  return vars as CSSProperties;
}
